import type { FC } from 'react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';

import { PostCard } from '@/features/posts/components/feed/PostCard';
import { PostCardActions } from '@/features/posts/components/feed/PostCardActions';
import { PostCardBody } from '@/features/posts/components/feed/PostCardBody';
import { PostProvider } from '@/features/posts/components/feed/PostCardContext';
import { PostCardHeader } from '@/features/posts/components/feed/PostCardHeader';
import { PostCardImage } from '@/features/posts/components/feed/PostCardImage';
import { PostHeaderInfo } from '@/features/posts/components/feed/PostHeaderInfo';
import { SectionContent } from '@/features/start/components/components/SectionContent';
import { useRegionNewsPost } from '@/features/start/hooks/useRegionNewsPost';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { AppRoute, TabRoute } from '@/navigation/types/navigation';
import { EmptyCard } from '@/shared/components/EmptyCard';
import { useRefetchOnFocus } from '@/shared/hooks/useRefetchOnFocus';

const styles = StyleSheet.create({
  loading: { paddingVertical: 24, alignItems: 'center' }
});

export const RegionPostsSection: FC = memo(() => {
  const { t } = useTranslation(LocalizationNameSpace.START, {
    keyPrefix: 'screens.start.sections.posts'
  });

  const regionNews = useRegionNewsPost();
  const { post, isLoading, isError } = regionNews;
  useRefetchOnFocus(regionNews);

  if (isLoading) {
    return (
      <SectionContent title={t('region')} testId="section-region-posts">
        <View style={styles.loading}>
          <ActivityIndicator size="large" />
        </View>
      </SectionContent>
    );
  }

  if (isError) {
    return (
      <SectionContent title={t('region')} testId="section-region-posts">
        <EmptyCard description={t('error')} mode="contained" />
      </SectionContent>
    );
  }

  if (!post) {
    return;
  }

  return (
    <SectionContent
      title={t('region')}
      testId="section-region-posts"
      screen={AppRoute.Tabs}
      params={{ screen: TabRoute.Region }}
    >
      <PostProvider value={post}>
        <PostCard>
          <PostCard.Content>
            <PostHeaderInfo />
            <PostCardImage />
            <PostCardHeader />
            <PostCardBody />
          </PostCard.Content>
          <PostCard.Actions>
            <PostCardActions />
          </PostCard.Actions>
        </PostCard>
      </PostProvider>
    </SectionContent>
  );
});

RegionPostsSection.displayName = 'RegionPostsSection';
